import { useEffect, useState } from 'react'

const DEMO_MODE_CHANGED_EVENT = 'veritact:demo-mode-changed'

/**
 * Pure predicate so we can unit-test the demo-vs-live decision without React.
 *
 * Demo video replay is the default on every device. Live camera mode is opt-in
 * via `?live=true`, set by the "Try it live" button on the scan instructions
 * screen (mobile) or by the QR-code consumer (phone scanning a desktop QR).
 *
 * `?fakelive=1` is preserved for the Playwright smoke and forces demo mode
 * regardless of `?live=true` — the smoke must never invoke a real camera.
 *
 * SSR safety: returns `true` (demo) for a missing window — demo is the
 * default everywhere, including any non-browser context. The SPA never
 * SSRs in practice; this is for test-runner safety only.
 */
export const isDemoMode = (win: Window | undefined): boolean => {
  if (!win) return true
  const params = new URLSearchParams(win.location.search)
  if (params.get('fakelive') === '1') return true
  if (params.get('live') === 'true') return false
  return true
}

/**
 * Returns whether the demo video replay path should run for this render.
 *
 * Listens to `veritact:demo-mode-changed` (dispatched by `setLiveMode`) and
 * `popstate` so the instructions screen re-renders in place when the user
 * taps "Try it live". The hook does NOT react to viewport resizes — the
 * mode is URL-driven only.
 */
export const useDemoMode = (): boolean => {
  const [demo, setDemo] = useState<boolean>(() =>
    isDemoMode(typeof window === 'undefined' ? undefined : window),
  )
  useEffect(() => {
    const onChange = (): void => setDemo(isDemoMode(window))
    window.addEventListener(DEMO_MODE_CHANGED_EVENT, onChange)
    window.addEventListener('popstate', onChange)
    return () => {
      window.removeEventListener(DEMO_MODE_CHANGED_EVENT, onChange)
      window.removeEventListener('popstate', onChange)
    }
  }, [])
  return demo
}

/**
 * Flips the current session into live mode by adding `?live=true` to the URL
 * and notifying all `useDemoMode` consumers. Uses `replaceState` (not push)
 * so the browser back button takes the user to the prior step rather than
 * to a stale demo-mode copy of the current step.
 */
export const setLiveMode = (): void => {
  const params = new URLSearchParams(window.location.search)
  params.set('live', 'true')
  window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`)
  window.dispatchEvent(new Event(DEMO_MODE_CHANGED_EVENT))
}

/**
 * Builds a URL that opts the consumer into live mode. Used by the desktop
 * QR-code path: the QR encodes the desktop's current URL with `live=true`
 * injected, preserving `step`, `brand`, and any other existing params.
 */
export const withLiveParam = (href: string): string => {
  const url = new URL(href)
  url.searchParams.set('live', 'true')
  return url.toString()
}

/**
 * Whether the interactive ("Try it live") affordance is enabled for this
 * session. Real Gemini Live testing is opt-in for a small allowlist of users
 * via `?interactive=true`; without it the "Try it live" button (mobile) and
 * QR code (desktop) are hidden, and only the demo replay is offered.
 *
 * Only the literal value `true` enables it — `?interactive=1`, `?interactive=yes`,
 * or a missing param all return `false`. The default parameter keeps the call
 * site argless while letting unit tests pass a fake window. SSR-safe: returns
 * `false` for a missing window, so the affordance is hidden by default.
 */
export const interactiveEnabled = (
  win: Window | undefined = typeof window === 'undefined' ? undefined : window,
): boolean => {
  if (!win) return false
  return new URLSearchParams(win.location.search).get('interactive') === 'true'
}
